/**
 * MandatoryUpdatePage — the blocking screen for a build below the
 * server's `minimum` threshold.
 *
 * Rendered by CacheBuster INSTEAD of the application, so it covers
 * every route including login; there is nothing behind it to reach.
 * Same AuthShell language as the maintenance page, for the same
 * reason: a hard stop should still look like the product.
 *
 * On the dashboard "updating" is simply reloading — the new build is
 * already on the server. The reload happens ONLY on the button, never
 * automatically: if a stale CDN copy keeps the build below the floor,
 * the user sees this screen again instead of the tab reloading
 * forever.
 *
 * Reloading here does not clear localStorage the way the optional
 * update does. Wiping it would sign the user out, and being forced to
 * log in again mid-shift is a poor reward for complying with a
 * mandatory update. Cache Storage is cleared, which is what actually
 * serves stale assets.
 */
import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { AuthShell, AuthCard } from '@widgets/auth-shell';
import { Button } from '@shared/ui';
import { RefreshIcon } from '@shared/ui/icons';
import './MandatoryUpdatePage.css';

const MandatoryUpdatePage = ({ currentName, attemptedReload }) => {
  const { titles, forms, dialogs, btn } = useSelector(state => state.i18n);
  const [isReloading, setIsReloading] = useState(false);

  const reload = useCallback(async () => {
    setIsReloading(true);
    try {
      if ('caches' in window) {
        const cacheNames = await window.caches.keys();
        await Promise.all(cacheNames.map(name => window.caches.delete(name)));
      }
    } catch {
      // A refused cache API (private mode, storage policy) is not a
      // reason to strand the user — reload regardless.
    }
    // Marks this attempt so a reload that lands on the same stale build
    // shows the "still not updated" hint rather than looking identical
    // to the first visit.
    try {
      sessionStorage.setItem('qhs.mandatoryUpdateReload', '1');
    } catch {
      // Storage unavailable: the hint is a nicety, not a requirement.
    }
    window.location.reload();
  }, []);

  return (
    <AuthShell
      brandWelcome={titles.welcomeToQHSLab}
      brandWelcomeStrong={titles.qhsLabBrand}
      brandTagline={forms.brandTagline}
      brandAttribution={forms.brandAttribution}
    >
      <AuthCard title={dialogs.mandatoryUpdateTitle} subtitle={dialogs.mandatoryUpdateSubtitle}>
        {attemptedReload ? (
          <p className="qhs-mandatory-update__retry">{dialogs.mandatoryUpdateStillOld}</p>
        ) : null}
        {currentName ? (
          <p className="qhs-mandatory-update__version">
            {dialogs.mandatoryUpdateVersionLabel} {currentName}
          </p>
        ) : null}
        <Button
          variant="primary"
          fullWidth
          startIcon={<RefreshIcon />}
          onClick={reload}
          loading={isReloading}
          disabled={isReloading}
        >
          {btn.reloadNow}
        </Button>
      </AuthCard>
    </AuthShell>
  );
};

export default MandatoryUpdatePage;
